import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import API_URL from "../api";
import { TabRubrosAnuales } from "./TabRubrosAnuales";

const MESES = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"
];

function mesLabel(yyyymm) {
  if (!yyyymm) return "";
  const [y, m] = yyyymm.split("-");
  return `${MESES[parseInt(m) - 1]} ${y}`;
}

function fmt(n) { return Number(n || 0).toFixed(2); }

const SEMAFORO = {
  verde:    { bg: "#0d2218", text: "#4caf50", label: "Al día" },
  amarillo: { bg: "#2a2200", text: "#ffc107", label: "Deuda baja" },
  rojo:     { bg: "#2a0d0d", text: "#e63946", label: "Deuda alta" },
};


const CAT_COLOR = {
  anticipo: { text: "#ffc107", bg: "#2a2000" },
  decimo:   { text: "#4cc9f0", bg: "#001a2a" },
  otro:     { text: "#888",    bg: "#1a1a1a" },
};

// ─── EmpleadoCard ─────────────────────────────────────────────────────────────
function EmpleadoCard({ emp, selected, onClick }) {
  const s = SEMAFORO[emp.semaforo] || SEMAFORO.verde;
  const iniciales = (emp.nombre_corto || emp.nombre)
    .split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
  return (
    <div onClick={onClick} style={{
      background: selected ? "#16213e" : "#1e1e2e",
      border: `1px solid ${selected ? "#4361ee" : "#2a2a3e"}`,
      borderLeft: `3px solid ${s.text}`,
      borderRadius: 10, padding: "14px 16px",
      cursor: "pointer", transition: "all .18s",
      display: "flex", alignItems: "center", gap: 12,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: "50%",
        background: `${s.text}22`, border: `1.5px solid ${s.text}44`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "monospace", fontWeight: 700, fontSize: 14,
        color: s.text, flexShrink: 0,
      }}>{iniciales}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: "#e0e0e0", fontWeight: 600, fontSize: 13,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {emp.nombre_corto || emp.nombre}
        </div>
        <div style={{ color: "#666", fontSize: 11, marginTop: 2 }}>{emp.cargo}</div>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ color: s.text, fontSize: 11, fontWeight: 700,
          background: s.bg, borderRadius: 4, padding: "2px 6px" }}>{s.label}</div>
        {emp.facturas_pendientes_total > 0 && (
          <div style={{ color: "#e63946", fontSize: 10, marginTop: 3 }}>
            Fac: ${fmt(emp.facturas_pendientes_total)}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── KPI ─────────────────────────────────────────────────────────────────────
function KPI({ label, value, color = "#4361ee" }) {
  return (
    <div style={{ background: "#1e1e2e", borderRadius: 10, padding: "14px 16px",
      borderTop: `3px solid ${color}` }}>
      <div style={{ color: "#aaa", fontSize: 12, marginBottom: 6 }}>{label}</div>
      <div style={{ color, fontSize: 22, fontWeight: "bold",
        fontFamily: "monospace" }}>${fmt(value)}</div>
    </div>
  );
}

function KPISmall({ label, value, color }) {
  return (
    <div style={{ background: "#1e1e2e", borderRadius: 8, padding: "8px 14px", textAlign: "center" }}>
      <div style={{ color: "#aaa", fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>{label}</div>
      <div style={{ color, fontFamily: "monospace", fontWeight: 700, fontSize: 14, marginTop: 3 }}>{value}</div>
    </div>
  );
}

function SecLabel({ children }) {
  return (
    <div style={{ color: "#aaa", fontSize: 11, textTransform: "uppercase",
      letterSpacing: 1, marginBottom: 8 }}>{children}</div>
  );
}

// ─── Tab Resumen ─────────────────────────────────────────────────────────────
function TabResumen({ emp, detalle }) {
  const rol = emp.rol || {};
  const valorRecibir = (rol.valor_ganado || 0) - (rol.iess || 0) - (rol.iess_prestamos || 0) - (rol.desc_quincena || 0);

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 20 }}>
        <KPI label="Sueldo base"     value={emp.sueldo_base} color="#4cc9f0" />
        <KPI label="Valor ganado"    value={rol.valor_ganado || emp.sueldo_base} color="#4caf50" />
        <KPI label="Valor a recibir" value={valorRecibir} color={valorRecibir >= 0 ? "#4cc9f0" : "#e63946"} />
        <KPI label="IESS"            value={rol.iess || 0} color="#e63946" />
        <KPI label="Desc. quincena"  value={rol.desc_quincena || 0} color="#f72585" />
        <KPI label="Fac. pendientes" value={emp.facturas_pendientes_total} color="#e63946" />
      </div>

      {detalle?.anticipos?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SecLabel>Anticipos del mes</SecLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {detalle.anticipos.map(a => (
              <div key={a.id} style={{ display: "flex", justifyContent: "space-between",
                background: "#1e1e2e", borderRadius: 6, padding: "8px 12px" }}>
                <div>
                  <div style={{ color: "#ccc", fontSize: 12 }}>{a.descripcion}</div>
                  <div style={{ color: "#555", fontSize: 10 }}>{a.fecha}</div>
                </div>
                <div style={{ color: "#ffc107", fontFamily: "monospace", fontWeight: 700 }}>${fmt(a.valor)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {detalle?.decimos?.length > 0 && (
        <div>
          <SecLabel>Décimos del mes</SecLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {detalle.decimos.map(a => (
              <div key={a.id} style={{ display: "flex", justifyContent: "space-between",
                background: "#1e1e2e", borderRadius: 6, padding: "8px 12px" }}>
                <div>
                  <div style={{ color: "#ccc", fontSize: 12 }}>{a.descripcion}</div>
                  <div style={{ color: "#555", fontSize: 10 }}>{a.fecha}</div>
                </div>
                <div style={{ color: "#4cc9f0", fontFamily: "monospace", fontWeight: 700 }}>${fmt(a.valor)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab Rol ─────────────────────────────────────────────────────────────────
function TabRol({ emp, detalle, mes, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [addDesc,   setAddDesc]   = useState(false);
  const [descForm,  setDescForm]  = useState({ tipo: "variable", descripcion: "", valor: "", factura_ref: "" });
  const [saving,    setSaving]    = useState(false);

  const rol = emp.rol || {};
  const valorRecibir = (rol.valor_ganado || 0) - (rol.iess || 0) - (rol.iess_prestamos || 0) - (rol.desc_quincena || 0);

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setUploadMsg("");
    const fd = new FormData();
    fd.append("archivo", file);
    try {
      const r = await fetch(`${API_URL}/empleados/rol/upload`, { method: "POST", body: fd });
      const d = await r.json();
      if (d.ok) {
        setUploadMsg(`✅ Rol ${mesLabel(d.mes)} cargado — ${d.insertados} nuevos, ${d.actualizados} actualizados`);
        onRefresh();
      } else { setUploadMsg(`❌ ${d.error}`); }
    } catch (err) { setUploadMsg(`❌ ${err.message}`); }
    setUploading(false);
  }

  async function handleSaveDesc() {
    if (!descForm.valor || parseFloat(descForm.valor) <= 0) return;
    setSaving(true);
    try {
      const d = await api.post("/empleados/descuentos", {
        cedula: emp.cedula, mes, tipo: descForm.tipo,
        descripcion: descForm.descripcion,
        valor: parseFloat(descForm.valor),
        factura_ref: descForm.factura_ref,
      });
      if (d.ok) {
        setAddDesc(false);
        setDescForm({ tipo: "variable", descripcion: "", valor: "", factura_ref: "" });
        onRefresh();
      }
    } catch {}
    setSaving(false);
  }

  async function handleDeleteDesc(id) {
    if (!window.confirm("¿Eliminar este descuento?")) return;
    await api.delete(`/empleados/descuentos/${id}`);
    onRefresh();
  }

  const tipo_color = { anticipo: "#ffc107", factura: "#e63946", variable: "#4cc9f0" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Upload */}
      <div style={{ background: "#1e1e2e", border: "1px dashed #2a2a3e",
        borderRadius: 10, padding: 16, display: "flex",
        justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>Subir Rol Mensual</div>
          <div style={{ color: "#aaa", fontSize: 11, marginTop: 2 }}>El mes se detecta automáticamente del Excel</div>
        </div>
        <label style={{ background: "#4361ee", color: "#fff", borderRadius: 8,
          padding: "8px 18px", fontSize: 12, fontWeight: 600,
          cursor: uploading ? "wait" : "pointer", whiteSpace: "nowrap" }}>
          {uploading ? "Subiendo..." : "📂 Subir Excel"}
          <input type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={handleUpload} />
        </label>
      </div>
      {uploadMsg && (
        <div style={{ color: uploadMsg.startsWith("✅") ? "#4caf50" : "#e63946",
          fontSize: 12, padding: "8px 12px", background: "#1e1e2e", borderRadius: 6 }}>
          {uploadMsg}
        </div>
      )}

      {/* Tabla del rol */}
      {rol.sueldo > 0 ? (
        <div>
          <SecLabel>Rol — {mesLabel(mes)}</SecLabel>
          <div style={{ background: "#1e1e2e", borderRadius: 10, overflow: "hidden" }}>
            {[
              ["Sueldo",           rol.sueldo,        null],
              ["Horas extras",     rol.horas_extras,  null],
              ["Bono",             rol.bono,           null],
              ["Fondo reserva",    rol.fondo_reserva, null],
              ["Valor ganado",     rol.valor_ganado,  "#4caf50"],
              ["IESS / Préstamos", rol.iess || 0,     "#e63946"],
              ["Desc. quincena",   rol.desc_quincena, "#f72585"],
            ].map(([label, val, color]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between",
                padding: "11px 16px", borderBottom: "1px solid #2a2a3e" }}>
                <span style={{ color: "#aaa", fontSize: 13 }}>{label}</span>
                <span style={{ color: color || "#fff", fontFamily: "monospace",
                  fontSize: 13, fontWeight: 600 }}>${fmt(val)}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between",
              padding: "14px 16px", background: valorRecibir >= 0 ? "#001a2a" : "#1a0000" }}>
              <span style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>Valor a recibir</span>
              <span style={{ color: valorRecibir >= 0 ? "#4cc9f0" : "#e63946",
                fontFamily: "monospace", fontSize: 16, fontWeight: 700 }}>
                ${fmt(valorRecibir)}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ color: "#555", fontSize: 12, textAlign: "center", padding: 20 }}>
          Sin rol cargado para {mesLabel(mes)}
        </div>
      )}

      {/* Descuentos */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between",
          alignItems: "center", marginBottom: 8 }}>
          <SecLabel>Descuentos del mes</SecLabel>
          <button onClick={() => setAddDesc(v => !v)} style={{
            background: "#4361ee22", border: "1px solid #4361ee44",
            color: "#4361ee", borderRadius: 6, padding: "5px 12px",
            fontSize: 11, cursor: "pointer" }}>+ Agregar manual</button>
        </div>

        {addDesc && (
          <div style={{ background: "#1e1e2e", borderRadius: 10, padding: 14,
            marginBottom: 10, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <select value={descForm.tipo}
                onChange={e => setDescForm(f => ({...f, tipo: e.target.value}))}
                style={inputStyle}>
                <option value="anticipo">Anticipo</option>
                <option value="factura">Factura</option>
                <option value="variable">Variable</option>
              </select>
              <input placeholder="Valor $" type="number" value={descForm.valor}
                onChange={e => setDescForm(f => ({...f, valor: e.target.value}))}
                style={inputStyle} />
            </div>
            <input placeholder="Descripción" value={descForm.descripcion}
              onChange={e => setDescForm(f => ({...f, descripcion: e.target.value}))}
              style={inputStyle} />
            <input placeholder="N° Factura Morpheus (opcional)" value={descForm.factura_ref}
              onChange={e => setDescForm(f => ({...f, factura_ref: e.target.value}))}
              style={inputStyle} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setAddDesc(false)}
                style={{ ...btnStyle, background: "#2a2a3e", color: "#aaa" }}>Cancelar</button>
              <button onClick={handleSaveDesc} disabled={saving}
                style={{ ...btnStyle, background: "#4361ee" }}>
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        )}

        {(detalle?.descuentos || []).length === 0 && !addDesc ? (
          <div style={{ color: "#555", fontSize: 12, textAlign: "center", padding: 12 }}>
            Sin descuentos registrados
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {(detalle?.descuentos || []).map(d => (
              <div key={d.id} style={{ display: "flex", justifyContent: "space-between",
                alignItems: "center", background: "#1e1e2e", borderRadius: 8,
                padding: "10px 14px" }}>
                <div style={{ flex: 1 }}>
                  <span style={{
                    background: `${tipo_color[d.tipo]}22`, color: tipo_color[d.tipo],
                    fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                    borderRadius: 3, padding: "2px 5px", marginRight: 6,
                  }}>{d.tipo}</span>
                  <span style={{ color: "#ccc", fontSize: 12 }}>{d.descripcion || "—"}</span>
                  {d.factura_ref && (
                    <span style={{ color: "#555", fontSize: 10, marginLeft: 6 }}>#{d.factura_ref}</span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ color: "#f72585", fontFamily: "monospace",
                    fontWeight: 700, fontSize: 13 }}>${fmt(d.valor)}</span>
                  <button onClick={() => handleDeleteDesc(d.id)} style={{
                    background: "none", border: "none", color: "#555",
                    cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0 }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab Anticipos (con décimos y otros) ─────────────────────────────────────
function TabAnticipos({ cedula, mes, onAgregarDescuento }) {
  const [data,      setData]      = useState(null);
  const [desde,     setDesde]     = useState("2026-01-01");
  const [mesActivo, setMesActivo] = useState(null);
  const [categoria, setCategoria] = useState("anticipos");
  const [agregando, setAgregando] = useState(null);

  const cargar = useCallback(() => {
    api.get(`/empleados/${cedula}/anticipos?desde=${desde}`).then(setData);
  }, [cedula, desde]);

  useEffect(() => { setMesActivo(null); cargar(); }, [cargar]);

  if (!data) return <div style={{ color: "#aaa", padding: 30, textAlign: "center" }}>Cargando...</div>;

  const listaActual   = data[categoria] || [];
  const listaFiltrada = mesActivo ? listaActual.filter(a => a.mes === mesActivo) : listaActual;
  const totalFiltrado = listaFiltrada.reduce((s, a) => s + a.valor, 0);

  async function handleAgregar(vale) {
    setAgregando(vale.id);
    await onAgregarDescuento({
      tipo: "anticipo",
      descripcion: vale.descripcion || "Anticipo",
      valor: vale.valor,
      factura_ref: "",
    });
    setAgregando(null);
  }

  const catLabels = {
    anticipos: `Anticipos (${data.anticipos?.length || 0})`,
    decimos:   `Décimos (${data.decimos?.length || 0})`,
    otros:     `Otros (${data.otros?.length || 0})`,
  };
  const catColors = { anticipos: "#ffc107", decimos: "#4cc9f0", otros: "#888" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ color: "#aaa", fontSize: 12 }}>Desde:</span>
          <input type="date" value={desde}
            onChange={e => { setDesde(e.target.value); setMesActivo(null); }}
            style={{ ...inputStyle, width: 140 }} />
          {mesActivo && (
            <button onClick={() => setMesActivo(null)} style={{
              background: "#4361ee22", border: "1px solid #4361ee44",
              color: "#4361ee", borderRadius: 5, padding: "4px 10px",
              fontSize: 11, cursor: "pointer" }}>✕ Ver todos</button>
          )}
        </div>
        <div style={{ color: catColors[categoria], fontFamily: "monospace",
          fontWeight: 700, fontSize: 16 }}>
          {mesActivo ? mesLabel(mesActivo) + ": " : "Total: "}${fmt(totalFiltrado)}
        </div>
      </div>

      {/* Selector de categoría */}
      <div style={{ display: "flex", gap: 0, background: "#1e1e2e",
        borderRadius: 8, overflow: "hidden" }}>
        {Object.entries(catLabels).map(([key, label]) => (
          <button key={key} onClick={() => { setCategoria(key); setMesActivo(null); }}
            style={{
              flex: 1, background: categoria === key ? `${catColors[key]}22` : "none",
              border: "none",
              borderBottom: `2px solid ${categoria === key ? catColors[key] : "transparent"}`,
              color: categoria === key ? catColors[key] : "#555",
              padding: "10px 8px", fontSize: 11, fontWeight: 600,
              cursor: "pointer", transition: "all .15s",
            }}>{label}</button>
        ))}
      </div>

      {/* Tarjetas de meses clickeables */}
      {data.por_mes?.length > 0 && (
        <div style={{ display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 8 }}>
          {data.por_mes.map(m => {
            const activo = mesActivo === m.mes;
            const val = categoria === "anticipos" ? m.anticipos :
                        categoria === "decimos"   ? m.decimos   : m.otros;
            if (!val && val !== 0) return null;
            return (
              <div key={m.mes}
                onClick={() => setMesActivo(p => p === m.mes ? null : m.mes)}
                style={{
                  background: "#1e1e2e",
                  border: `1px solid ${activo ? catColors[categoria] + "55" : "#2a2a3e"}`,
                  borderTop: `3px solid ${activo ? catColors[categoria] : "#2a2a3e"}`,
                  borderRadius: 10, padding: "10px 12px",
                  textAlign: "center", cursor: "pointer", transition: "all .15s",
                }}>
                <div style={{ color: "#aaa", fontSize: 10 }}>{mesLabel(m.mes)}</div>
                <div style={{ color: activo ? catColors[categoria] : "#fff",
                  fontFamily: "monospace", fontWeight: 700,
                  fontSize: 15, marginTop: 3 }}>${fmt(val)}</div>
                <div style={{ color: "#555", fontSize: 10 }}>{m.count} mov.</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lista de vales */}
      {listaFiltrada.length === 0 ? (
        <div style={{ color: "#555", textAlign: "center", padding: 24, fontSize: 12 }}>
          Sin registros en el período
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {listaFiltrada.map(a => {
            const cc = CAT_COLOR[a.categoria] || CAT_COLOR.otro;
            const esMesActual = a.mes === mes;
            return (
              <div key={a.id} style={{
                background: "#1e1e2e", borderRadius: 8, padding: "10px 14px",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between",
                  alignItems: "center" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ background: cc.bg, color: cc.text,
                        fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                        borderRadius: 3, padding: "2px 5px" }}>{a.categoria}</span>
                      <span style={{ color: "#ccc", fontSize: 12 }}>{a.descripcion || "Vale"}</span>
                    </div>
                    <div style={{ color: "#555", fontSize: 10, marginTop: 3 }}>
                      Vale #{a.numero} · {a.fecha}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ color: cc.text, fontFamily: "monospace",
                      fontWeight: 700, fontSize: 14 }}>${fmt(a.valor)}</span>
                    {a.categoria === "anticipo" && esMesActual && (
                      <button disabled={agregando === a.id} onClick={() => handleAgregar(a)}
                        style={{
                          background: "#ffc10718", border: "1px solid #ffc10733",
                          color: "#ffc107", borderRadius: 5,
                          padding: "4px 10px", fontSize: 10,
                          cursor: agregando === a.id ? "wait" : "pointer",
                          whiteSpace: "nowrap",
                        }}>
                        {agregando === a.id ? "..." : "⬇ Al descuento"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Tab Facturas ─────────────────────────────────────────────────────────────
function TabFacturas({ cedula, onMarcarDesc, mes }) {
  const [data,     setData]     = useState(null);
  const [soloPend, setSoloPend] = useState(false);
  const [marcando, setMarcando] = useState(null);

  useEffect(() => {
    api.get(`/empleados/${cedula}/facturas?pendientes=${soloPend}`).then(setData);
  }, [cedula, soloPend]);

  if (!data) return <div style={{ color: "#aaa", padding: 20, textAlign: "center" }}>Cargando...</div>;
  const facturas = data.facturas || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 8 }}>
          <KPISmall label="Pendiente" value={`$${fmt(data.total_pendiente)}`} color="#e63946" />
          <KPISmall label="Historial" value={`$${fmt(data.total_historico)}`} color="#4361ee" />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6,
          color: "#aaa", fontSize: 12, cursor: "pointer" }}>
          <input type="checkbox" checked={soloPend}
            onChange={e => setSoloPend(e.target.checked)} />
          Solo pendientes
        </label>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {facturas.map(f => (
          <div key={f.factura} style={{
            background: "#1e1e2e",
            border: `1px solid ${f.pendiente ? "#2a1a1a" : "#2a2a3e"}`,
            borderRadius: 10, padding: "12px 16px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between",
              alignItems: "flex-start" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: "#aaa", fontFamily: "monospace", fontSize: 11 }}>
                    {f.factura}
                  </span>
                  <span style={{
                    fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                    borderRadius: 3, padding: "2px 5px",
                    background: f.forma_pago === "CREDITO" ? "#e6394622" : "#4caf5022",
                    color: f.forma_pago === "CREDITO" ? "#e63946" : "#4caf50",
                  }}>{f.forma_pago}</span>
                </div>
                <div style={{ color: "#555", fontSize: 10, marginTop: 3 }}>{f.fecha}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ color: "#fff", fontFamily: "monospace",
                  fontSize: 13, fontWeight: 600 }}>${fmt(f.total)}</div>
                {f.pendiente && (
                  <div style={{ color: "#e63946", fontSize: 11, marginTop: 1 }}>
                    Saldo: ${fmt(f.saldo)}
                  </div>
                )}
              </div>
            </div>
            {f.pendiente && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #2a2a3e" }}>
                <button disabled={marcando === f.factura}
                  onClick={async () => {
                    setMarcando(f.factura);
                    await onMarcarDesc({
                      tipo: "factura",
                      descripcion: `Factura ${f.factura}`,
                      valor: f.saldo, factura_ref: f.factura,
                    });
                    setMarcando(null);
                  }}
                  style={{
                    background: "#e6394618", border: "1px solid #e6394633",
                    color: "#e63946", borderRadius: 6, padding: "6px 14px", fontSize: 11,
                    cursor: marcando === f.factura ? "wait" : "pointer",
                  }}>
                  {marcando === f.factura ? "Agregando..." : "⬇ Descontar en quincena"}
                </button>
              </div>
            )}
          </div>
        ))}
        {facturas.length === 0 && (
          <div style={{ color: "#555", textAlign: "center", padding: 20, fontSize: 12 }}>
            Sin facturas registradas
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Estilos ──────────────────────────────────────────────────────────────────
const inputStyle = {
  background: "#2a2a3e", border: "1px solid #3a3a5e", borderRadius: 6,
  color: "#fff", padding: "8px 10px", fontSize: 12, outline: "none", width: "100%",
};
const btnStyle = {
  border: "none", borderRadius: 6, color: "#fff",
  padding: "7px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer",
};

// ─── Componente principal ─────────────────────────────────────────────────────
export default function Empleados() {
  const hoy = new Date();
  const mesActual = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`;

  const [mes,        setMes]        = useState(mesActual);
  const [empleados,  setEmpleados]  = useState([]);
  const [selected,   setSelected]   = useState(null);
  const [detalle,    setDetalle]    = useState(null);
  const [activeTab,  setActiveTab]  = useState("resumen");
  const [loading,    setLoading]    = useState(true);
  const [loadingDet, setLoadingDet] = useState(false);

  const cargarEmpleados = useCallback(() => {
    setLoading(true);
    api.get(`/empleados?mes=${mes}`)
      .then(d => { setEmpleados(d.empleados || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [mes]);

  const cargarDetalle = useCallback((cedula) => {
    if (!cedula) return;
    setLoadingDet(true);
    api.get(`/empleados/${cedula}?mes=${mes}`)
      .then(d => { setDetalle(d); setLoadingDet(false); })
      .catch(() => setLoadingDet(false));
  }, [mes]);

  useEffect(() => { cargarEmpleados(); }, [cargarEmpleados]);
  useEffect(() => { if (selected) cargarDetalle(selected.cedula); }, [selected, cargarDetalle]);

  function handleSelectEmp(emp) {
    setSelected(emp); setActiveTab("resumen"); setDetalle(null);
  }

  async function handleMarcarDesc(payload) {
    if (!selected) return;
    await api.post("/empleados/descuentos", { cedula: selected.cedula, mes, ...payload });
    cargarDetalle(selected.cedula);
    cargarEmpleados();
  }

  function onRefresh() {
    cargarEmpleados();
    if (selected) cargarDetalle(selected.cedula);
  }

  const mesesOpts = [];
  for (let i = -5; i <= 2; i++) {
    const d = new Date(hoy.getFullYear(), hoy.getMonth() + i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    mesesOpts.push({ val, label: mesLabel(val) });
  }

  const tabs = ["resumen", "rol", "anticipos", "facturas", "rubros"];
  const tabLabel = {
    resumen: "Resumen", rol: "Rol & Descuentos",
    anticipos: "Anticipos / Décimos", facturas: "Facturas", rubros: "📋 Rubros Anuales",
  };

  const empSelected = selected
    ? empleados.find(e => e.cedula === selected.cedula) || selected
    : null;

  return (
    <div style={{ display: "flex", gap: 0, fontFamily: "'Segoe UI', sans-serif", color: "#e0e0e0" }}>

      {/* Sidebar */}
      <div style={{ width: 270, flexShrink: 0, borderRight: "1px solid #2a2a3e" }}>
        <div style={{ padding: "20px 16px 12px" }}>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>Empleados</div>
          <div style={{ color: "#555", fontSize: 11, marginTop: 2 }}>{empleados.length} activos</div>
        </div>
        <div style={{ padding: "8px 12px", borderBottom: "1px solid #2a2a3e" }}>
          <select value={mes} onChange={e => { setMes(e.target.value); setSelected(null); }}
            style={{ ...inputStyle, fontSize: 11 }}>
            {mesesOpts.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
          </select>
        </div>
        <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 6 }}>
          {loading ? (
            <div style={{ color: "#555", textAlign: "center", padding: 30, fontSize: 12 }}>Cargando...</div>
          ) : empleados.map(emp => (
            <EmpleadoCard key={emp.cedula} emp={emp}
              selected={selected?.cedula === emp.cedula}
              onClick={() => handleSelectEmp(emp)} />
          ))}
        </div>
        {empleados.length > 0 && (
          <div style={{ padding: "10px 16px", borderTop: "1px solid #2a2a3e" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#555", fontSize: 11 }}>Facturas pend.</span>
              <span style={{ color: "#e63946", fontFamily: "monospace",
                fontSize: 12, fontWeight: 700 }}>
                ${fmt(empleados.reduce((s,e) => s + (e.facturas_pendientes_total||0), 0))}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Panel derecho */}
      {!empSelected ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center",
          justifyContent: "center", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 40 }}>👤</div>
          <div style={{ color: "#555", fontSize: 13 }}>Selecciona un empleado</div>
        </div>
      ) : (
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #2a2a3e",
            display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>
                {empSelected.nombre_corto || empSelected.nombre}
              </div>
              <div style={{ color: "#555", fontSize: 12, marginTop: 3 }}>
                {empSelected.cargo} · C.I. {empSelected.cedula}
              </div>
            </div>
            <div style={{
              background: SEMAFORO[empSelected.semaforo]?.bg,
              color: SEMAFORO[empSelected.semaforo]?.text,
              border: `1px solid ${SEMAFORO[empSelected.semaforo]?.text}44`,
              borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700,
            }}>{SEMAFORO[empSelected.semaforo]?.label}</div>
          </div>

          <div style={{ display: "flex", borderBottom: "1px solid #2a2a3e", padding: "0 20px" }}>
            {tabs.map(t => (
              <button key={t} onClick={() => setActiveTab(t)} style={{
                background: "none", border: "none", cursor: "pointer",
                color: activeTab === t ? "#4361ee" : "#555",
                fontWeight: activeTab === t ? 700 : 400,
                fontSize: 12, padding: "12px 14px",
                borderBottom: `2px solid ${activeTab === t ? "#4361ee" : "transparent"}`,
                transition: "all .15s",
              }}>{tabLabel[t]}</button>
            ))}
          </div>

          <div style={{ padding: "20px 24px", overflowY: "auto" }}>
            {loadingDet ? (
              <div style={{ color: "#555", textAlign: "center", padding: 40, fontSize: 12 }}>
                Cargando detalle...
              </div>
            ) : (
              <>
                {activeTab === "resumen"   && <TabResumen emp={empSelected} detalle={detalle} />}
                {activeTab === "rol"       && <TabRol emp={empSelected} detalle={detalle} mes={mes} onRefresh={onRefresh} />}
                {activeTab === "anticipos" && <TabAnticipos cedula={empSelected.cedula} mes={mes} onAgregarDescuento={handleMarcarDesc} />}
                {activeTab === "facturas"  && <TabFacturas cedula={empSelected.cedula} mes={mes} onMarcarDesc={handleMarcarDesc} />}
                {activeTab === "rubros" && <TabRubrosAnuales />}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}